import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/layout/Navbar";
import DashboardHeader from "@/components/layout/DashboardHeader";
import Footer from "@/components/layout/Footer";
import PartnersStrip from "@/components/PartnersStrip";
import { Plus, Search, MapPin, Globe, Instagram, X, Mail, Briefcase, Edit, Trash2, ExternalLink } from "lucide-react";
import { PinButton } from "@/components/PinContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const TAG_OPTIONS = [
  "Hiring",
  "Looking to collab",
  "BIPOC-owned",
  "LGBTQ+ owned",
  "Sustainable",
  "Locally sourced",
];

const canAddBusiness = (u) =>
  !!u && (u.is_admin || u.is_founder || u.is_founding_member || ["member", "collective", "paying", "pro"].includes(u.membership_tier));

const EMPTY_FORM = { name: "", tagline: "", description: "", industry: "", industry_other: "", logo_url: "", cover_url: "", website: "", instagram: "", location: "", tags: [] };

const Businesses = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [interestForm, setInterestForm] = useState({ email: "", business_name: "" });
  const [interestSent, setInterestSent] = useState(false);

  const [industries, setIndustries] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    axios.get(`${API}/profile-options`).then((r) => setIndustries(r.data.industries || [])).catch(() => {});
    axios.get(`${API}/businesses`).then((r) => setBusinesses(r.data))
      .catch(() => toast.error("Failed to load businesses"))
      .finally(() => setLoading(false));
  }, [user]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (b) => {
    setEditingId(b.business_id);
    setForm({
      name: b.name || "",
      tagline: b.tagline || "",
      description: b.description || "",
      industry: industries.includes(b.industry) ? b.industry : (b.industry ? "Other" : ""),
      industry_other: industries.includes(b.industry) ? "" : (b.industry || ""),
      logo_url: b.logo_url || "",
      cover_url: b.cover_url || "",
      website: b.website || "",
      instagram: b.instagram || "",
      location: b.location || "",
      tags: b.tags || [],
    });
    setOpen(true);
  };

  const removeBusiness = async (id) => {
    if (!window.confirm("Delete this business listing?")) return;
    try {
      await axios.delete(`${API}/businesses/${id}`);
      setBusinesses(businesses.filter((b) => b.business_id !== id));
      toast.success("Removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const filtered = businesses.filter((b) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [b.name, b.tagline, b.industry, b.location, ...(b.tags || [])]
      .filter(Boolean)
      .some((s) => s.toLowerCase().includes(q));
  });

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload/business-logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, logo_url: r.data.url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload/business-logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, cover_url: r.data.url }));
      toast.success("Cover photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const toggleTag = (t) => setForm((p) => ({
    ...p, tags: p.tags.includes(t) ? p.tags.filter((x) => x !== t) : [...p.tags, t]
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Business name is required"); return; }
    setSaving(true);
    try {
      // Resolve industry: if "Other", use industry_other text
      const industryFinal = form.industry === "Other" ? (form.industry_other || "Other") : form.industry;
      const payload = { ...form, industry: industryFinal };
      delete payload.industry_other;

      if (editingId) {
        const r = await axios.put(`${API}/businesses/${editingId}`, payload);
        setBusinesses(businesses.map((b) => b.business_id === editingId ? r.data : b));
        toast.success("Business updated");
      } else {
        const r = await axios.post(`${API}/businesses`, payload);
        setBusinesses([r.data, ...businesses]);
        toast.success("Business added!");
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const submitInterest = async (e) => {
    e.preventDefault();
    if (!interestForm.email) return;
    try {
      await axios.post(`${API}/businesses/interest`, interestForm);
      setInterestSent(true);
      toast.success("You're on the list!");
    } catch {
      toast.error("Something went wrong");
    }
  };

  // Logged-out state — show only the CTA hero with signup
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]" data-testid="businesses-page">
        <Navbar />
        <section className="bg-[#0A0A0A] py-16 md:py-24 border-b-2 border-[#0A0A0A]">
          <div className="container-main">
            <div className="max-w-3xl">
              <p className="overline text-[#FF2D87] mb-4 inline-flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Business Directory
              </p>
              <h1 className="heading-hero mb-6 text-white">
                Hiring or looking to <span className="text-[#FF2D87]">collaborate?</span>
              </h1>
              <p className="text-lg text-white/80 mb-8">
                Sign up now and receive updates as we launch our directory celebrating small and local businesses,
                with a special focus on those founded and led by female, gender diverse, and BIPOC communities.
              </p>
              {interestSent ? (
                <div className="bg-[#FFC2E8] border-2 border-[#FFC2E8] inline-block px-6 py-4">
                  <p className="font-bold text-[#0A0A0A]">Thanks — we'll keep you posted.</p>
                </div>
              ) : (
                <form onSubmit={submitInterest} className="max-w-xl flex flex-col sm:flex-row gap-3" data-testid="interest-form">
                  <input
                    type="text"
                    placeholder="Business name (optional)"
                    value={interestForm.business_name}
                    onChange={(e) => setInterestForm({ ...interestForm, business_name: e.target.value })}
                    className="px-4 py-3 bg-white border-2 border-[#0A0A0A] focus:outline-none focus:border-[#FF2D87] text-sm flex-1 text-[#0A0A0A]"
                    data-testid="interest-business-name"
                  />
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={interestForm.email}
                    onChange={(e) => setInterestForm({ ...interestForm, email: e.target.value })}
                    required
                    className="px-4 py-3 bg-white border-2 border-[#0A0A0A] focus:outline-none focus:border-[#FF2D87] text-sm flex-1 text-[#0A0A0A]"
                    data-testid="interest-email"
                  />
                  <button type="submit" className="bg-[#FF2D87] text-white font-bold rounded-full px-6 py-3 hover:bg-white hover:text-[#0A0A0A] transition-colors text-xs uppercase tracking-widest" data-testid="interest-submit">
                    Notify Me
                  </button>
                </form>
              )}
              <p className="text-xs text-white/60 mt-6">
                Already a member? <Link to="/login" className="font-bold underline text-[#FFC2E8]">Sign in</Link> to view the directory.
              </p>
            </div>
          </div>
        </section>
        <PartnersStrip />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]" data-testid="businesses-page">
      <Navbar />
      <DashboardHeader />

      {/* Section heading — slim, contained */}
      <section className="pt-8 md:pt-10 pb-2">
        <div className="container-main">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="overline text-[#FF2D87] mb-2 inline-flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Business Directory
              </p>
              <p className="text-sm md:text-base text-[#4A4A4A] max-w-2xl">
                A curated directory celebrating small and local businesses — with a special focus on those founded and led by female, gender diverse, and BIPOC communities.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {canAddBusiness(user) ? (
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY_FORM); } }}>
                  <button
                    onClick={openCreate}
                    className="bg-[#0A0A0A] text-white font-bold rounded-full px-5 py-2.5 hover:bg-[#FF2D87] transition-colors text-xs uppercase tracking-widest inline-flex items-center gap-2"
                    data-testid="add-business-btn"
                  >
                    <Plus className="w-4 h-4" /> Add your business
                  </button>
                  <DialogContent className="dialog-content sm:max-w-xl" data-testid="add-business-modal">
                    <DialogHeader>
                      <DialogTitle className="text-3xl font-display font-bold">{editingId ? "Edit business" : "List your business"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4 mt-2">
                      <div>
                        <label className="form-label">Cover photo (optional)</label>
                        {form.cover_url && (
                          <div className="mb-2">
                            <img src={form.cover_url} alt="cover" className="w-full h-32 object-cover border-2 border-[#0A0A0A]" />
                          </div>
                        )}
                        <input type="file" accept="image/*" onChange={handleCover} disabled={uploading} className="text-sm" data-testid="biz-cover-input" />
                      </div>
                      <div>
                        <label className="form-label">Logo</label>
                        <div className="flex items-center gap-4">
                          <input type="file" accept="image/*" onChange={handleLogo} disabled={uploading} className="text-sm" data-testid="biz-logo-input" />
                          {form.logo_url && <img src={form.logo_url} alt="logo" className="h-12 max-w-[120px] object-contain border-2 border-[#0A0A0A]" />}
                        </div>
                      </div>
                      <input type="text" required placeholder="Business name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-underline" data-testid="biz-name-input" />
                      <input type="text" placeholder="One-line tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="input-underline" data-testid="biz-tagline-input" />
                      <textarea placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="form-textarea" data-testid="biz-description-input" />

                      <div>
                        <label className="form-label">Industry</label>
                        <select
                          value={form.industry}
                          onChange={(e) => setForm({ ...form, industry: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-[#0A0A0A] bg-white focus:outline-none focus:border-[#FF2D87] text-sm"
                          data-testid="biz-industry-select"
                        >
                          <option value="">Select an industry…</option>
                          {industries.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
                          <option value="Other">Other (specify)</option>
                        </select>
                        {form.industry === "Other" && (
                          <input
                            type="text"
                            placeholder="Type your industry"
                            value={form.industry_other}
                            onChange={(e) => setForm({ ...form, industry_other: e.target.value })}
                            className="input-underline mt-2"
                            data-testid="biz-industry-other-input"
                          />
                        )}
                      </div>

                      <input type="text" placeholder="Location (city, state)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input-underline" data-testid="biz-location-input" />
                      <input type="url" placeholder="Website URL" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="input-underline" data-testid="biz-website-input" />
                      <input type="text" placeholder="Instagram @handle" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className="input-underline" data-testid="biz-instagram-input" />

                      <div>
                        <label className="form-label">Tags</label>
                        <div className="flex flex-wrap gap-2">
                          {TAG_OPTIONS.map((t) => (
                            <button type="button" key={t} onClick={() => toggleTag(t)}
                              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full border-2 border-[#0A0A0A] transition-colors ${form.tags.includes(t) ? "bg-[#FF2D87] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#FFC2E8]"}`}
                              data-testid={`biz-tag-${t.toLowerCase().replace(/\s+|[^a-z0-9-]/g, '-')}`}>
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>

                      <button type="submit" disabled={saving || uploading} className="btn-primary w-full" data-testid="biz-submit-btn">
                        {saving ? "Saving…" : (editingId ? "Save Changes" : "Add Business")}
                      </button>
                    </form>
                  </DialogContent>
                </Dialog>
              ) : (
                <div className="inline-flex flex-wrap items-center gap-3 bg-[#FFC2E8] border-2 border-[#0A0A0A] px-5 py-4 text-[#0A0A0A]" data-testid="premium-cta">
                  <span className="text-sm">
                    <strong>Add your business</strong> — available to Founding Members & paid tiers. Stripe tiers coming soon.
                  </span>
                  <a
                    href="https://ko-fi.com/thecreativeensemble"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#FF2D87] text-white font-bold rounded-full px-4 py-2 hover:bg-[#0A0A0A] transition-colors text-xs uppercase tracking-widest inline-flex items-center gap-2 whitespace-nowrap"
                    data-testid="ko-fi-cta"
                  >
                    Become a Founding Member
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Search + Grid */}
      <section className="py-10 md:py-14">
        <div className="container-main">
          <div className="max-w-md mb-8 relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, industry, tag, location…"
              className="w-full pl-11 pr-4 py-3 bg-white border-2 border-[#0A0A0A] focus:outline-none focus:border-[#FF2D87] text-sm text-[#0A0A0A]"
              data-testid="businesses-search" />
          </div>

          {loading ? (
            <p className="text-[#0A0A0A]/70">Loading…</p>
          ) : filtered.length === 0 ? (
            <div className="bg-white border-2 border-[#0A0A0A] p-10 text-center text-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)]">
              <p className="font-display text-2xl mb-2 font-bold">{businesses.length === 0 ? "No businesses yet." : "No matches."}</p>
              <p className="text-sm text-[#0A0A0A]/70">{businesses.length === 0 ? "Be the first to list yours." : "Try a different search."}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
              {filtered.map((b) => (
                <BusinessCard key={b.business_id} business={b} currentUser={user} onEdit={() => openEdit(b)} onDelete={() => removeBusiness(b.business_id)} />
              ))}
            </div>
          )}
        </div>
      </section>

      <PartnersStrip />
      <Footer />
    </div>
  );
};

const BusinessCard = ({ business, currentUser, onEdit, onDelete }) => {
  const showContact = currentUser && (currentUser.is_admin || currentUser.is_founder || currentUser.is_founding_member || currentUser.membership_tier !== "free");
  const canEdit = currentUser && (currentUser.user_id === business.owner_id || currentUser.is_admin);
  return (
    <div className="bg-[#0A0A0A] rounded-sm p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl flex flex-col h-full text-white relative group" data-testid={`business-${business.business_id}`}>
      {!canEdit && currentUser && (
        <div className="absolute top-3 right-3 z-10">
          <PinButton targetType="business" targetId={business.business_id} className="w-8 h-8" />
        </div>
      )}
      {canEdit && (
        <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <button onClick={onEdit} className="w-8 h-8 bg-white text-[#0A0A0A] flex items-center justify-center hover:bg-[#5170FF] hover:text-white transition-colors" aria-label="Edit" data-testid={`edit-biz-${business.business_id}`}>
            <Edit className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="w-8 h-8 bg-white text-[#0A0A0A] flex items-center justify-center hover:bg-[#FF2D87] hover:text-white transition-colors" aria-label="Delete" data-testid={`delete-biz-${business.business_id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="flex flex-col items-center text-center flex-1">
        {/* Logo — square, white outline */}
        <div className="w-20 h-20 overflow-hidden border-2 border-white mb-4">
          {business.logo_url ? (
            <img src={business.logo_url} alt={business.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-[#5170FF] flex items-center justify-center text-white text-2xl font-display font-bold">
              {business.name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Name — heavy display, blue (mirrors pink in personal cards) */}
        <h3 className="text-lg font-display font-bold text-[#5170FF] mb-1 break-words">
          {business.name}
        </h3>

        {/* Owner — links to owner profile (replaces 'View Owner Profile' link) */}
        {business.owner_name && (
          <Link
            to={`/profile/${business.owner_id}`}
            className="text-sm text-white mb-2 px-2 hover:text-[#5170FF] transition-colors"
          >
            by <span className="font-semibold underline">{business.owner_name}</span>
          </Link>
        )}

        {/* Location — pink (mirrors blue location in personal cards) */}
        {business.location && (
          <div className="flex items-center gap-1 text-xs text-[#FF2D87] mb-3">
            <MapPin className="w-3 h-3" />
            {business.location}
          </div>
        )}

        {/* Industry as a small bubble */}
        {business.industry && (
          <div className="flex flex-wrap justify-center gap-1.5 mb-3">
            <span className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#5170FF] text-white">
              {business.industry}
            </span>
          </div>
        )}

        {/* Tagline */}
        {business.tagline && (
          <p className="text-sm font-semibold text-white mb-2 px-2">{business.tagline}</p>
        )}

        {/* Description excerpt */}
        {business.description && (
          <p className="text-xs text-white/60 line-clamp-2 mb-4 px-2">{business.description}</p>
        )}

        {/* Tag badges — moved below business info, now baby pink */}
        {(business.tags || []).length > 0 && (
          <div className="flex gap-2 mb-2 flex-wrap justify-center">
            {business.tags.map((t) => (
              <span key={t} className="text-[9px] font-bold uppercase tracking-widest bg-[#FFC2E8] text-[#0A0A0A] px-2.5 py-1 rounded-full">
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Footer — pinned to bottom for alignment (matches MemberCard) */}
      <div className="mt-auto pt-4 flex flex-col items-center gap-3 border-t border-white/10">
        {/* Social icons — pink outlined (mirrors blue icons in personal cards) */}
        <div className="flex gap-2 items-center min-h-[36px]">
          {showContact && business.website && (
            <a
              href={business.website}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full border-2 border-[#FF2D87] flex items-center justify-center text-[#FF2D87] hover:bg-[#FF2D87] hover:text-white transition-colors"
              aria-label="Website"
            >
              <Globe className="w-4 h-4" />
            </a>
          )}
          {showContact && business.instagram && (
            <a
              href={`https://instagram.com/${business.instagram.replace("@", "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full border-2 border-[#FF2D87] flex items-center justify-center text-[#FF2D87] hover:bg-[#FF2D87] hover:text-white transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* View Profile — links to business detail */}
        <Link
          to={`/business/${business.business_id}`}
          className="text-xs font-bold text-[#5170FF] hover:text-white flex items-center gap-1 transition-colors"
          data-testid={`view-business-${business.business_id}`}
        >
          View Profile <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
};

export default Businesses;
